import { useTranslate } from "ra-core";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { TaskType } from "../types";
import { NEXT_STEP_TYPES } from "./nextStep";

export type NextStepDraft = {
  title: string;
  type: TaskType;
  due_date: string;
};

export const TASK_TYPE_FALLBACK: Record<TaskType, string> = {
  call: "Ring",
  meeting: "Møte",
  site_visit: "Befaring",
  email: "E-post",
  task: "Oppgave",
  revisit: "Revisit",
  review: "Be om anmeldelse",
  billing: "Fakturering",
};

export const useTaskTypeLabel = () => {
  const translate = useTranslate();
  return (type: TaskType | null | undefined) =>
    type
      ? translate(`crm.tasks.type.${type}`, { _: TASK_TYPE_FALLBACK[type] })
      : translate("crm.tasks.type.none", { _: "Vanlig oppgave" });
};

const selectClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30";

// Hva, type og når for et neste steg. Brukes i «Hva er neste steg?» og i
// stadieporten.
export const NextStepFields = ({
  value,
  onChange,
  idPrefix,
  autoFocus,
  errors,
}: {
  value: NextStepDraft;
  onChange: (value: NextStepDraft) => void;
  idPrefix: string;
  autoFocus?: boolean;
  errors?: { title?: string; due_date?: string };
}) => {
  const translate = useTranslate();
  const typeLabel = useTaskTypeLabel();
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_9rem_10rem]">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-title`}>
          {translate("crm.deals.next.what", { _: "Hva gjør vi?" })}
        </Label>
        <Input
          id={`${idPrefix}-title`}
          value={value.title}
          autoFocus={autoFocus}
          aria-invalid={!!errors?.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
        />
        {errors?.title && (
          <p className="text-xs text-destructive">{errors.title}</p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-type`}>
          {translate("crm.tasks.type.label", { _: "Type" })}
        </Label>
        <select
          id={`${idPrefix}-type`}
          className={selectClass}
          value={value.type}
          onChange={(e) =>
            onChange({ ...value, type: e.target.value as TaskType })
          }
        >
          {NEXT_STEP_TYPES.map((type) => (
            <option key={type} value={type}>
              {typeLabel(type)}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-date`}>
          {translate("crm.deals.next.when", { _: "Når?" })}
        </Label>
        <Input
          id={`${idPrefix}-date`}
          type="date"
          value={value.due_date}
          aria-invalid={!!errors?.due_date}
          onChange={(e) => onChange({ ...value, due_date: e.target.value })}
        />
        {errors?.due_date && (
          <p className="text-xs text-destructive">{errors.due_date}</p>
        )}
      </div>
    </div>
  );
};

export const validateNextStep = (
  draft: NextStepDraft,
  translate: (key: string, options?: Record<string, unknown>) => string,
) => {
  const errors: { title?: string; due_date?: string } = {};
  if (!draft.title.trim())
    errors.title = translate("crm.deals.next.need_title", {
      _: "Skriv hva neste steg er.",
    });
  if (!draft.due_date)
    errors.due_date = translate("crm.deals.next.need_date", {
      _: "Velg en dato.",
    });
  return errors;
};
